import React, { useState, useEffect } from 'react';
import { db, functions } from '../../../utils/firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Play, Mail, Clock, CheckCircle, XCircle, RefreshCw, TestTube, Zap } from 'lucide-react';

interface AutomationQueueItem {
  id: string;
  automationId: string;
  orderId: string;
  email: string;
  templateKey: string;
  subject: string;
  scheduledFor: any;
  status: 'pending' | 'sent' | 'failed';
  createdAt: any;
  sentAt?: any;
  error?: string;
}

interface AutomationStats {
  pending: number;
  sent: number;
  failed: number;
}

const AutomationManager: React.FC = () => {
  const [queueItems, setQueueItems] = useState<AutomationQueueItem[]>([]);
  const [stats, setStats] = useState<AutomationStats>({ pending: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'sent' | 'failed'>('all');

  // Test automation state
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [sendImmediately, setSendImmediately] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Process Now state
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    fetchQueueItems();
    fetchStats();
  }, [filter]);

  const fetchQueueItems = async () => {
    setLoading(true);
    try {
      let q = query(
        collection(db, 'automationQueue'),
        orderBy('scheduledFor', 'desc'),
        limit(100)
      );

      if (filter !== 'all') {
        q = query(
          collection(db, 'automationQueue'),
          where('status', '==', filter),
          orderBy('scheduledFor', 'desc'),
          limit(100)
        );
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AutomationQueueItem[];

      setQueueItems(items);
    } catch (error) {
      console.error('Error fetching queue items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const pendingSnapshot = await getDocs(
        query(collection(db, 'automationQueue'), where('status', '==', 'pending'))
      );
      const sentSnapshot = await getDocs(
        query(collection(db, 'automationQueue'), where('status', '==', 'sent'))
      );
      const failedSnapshot = await getDocs(
        query(collection(db, 'automationQueue'), where('status', '==', 'failed'))
      );

      setStats({
        pending: pendingSnapshot.size,
        sent: sentSnapshot.size,
        failed: failedSnapshot.size
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTestAutomation = async () => {
    if (!testEmail) {
      setTestResult({ success: false, message: 'Email is required' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const testFn = httpsCallable(functions, 'testPostPurchaseAutomation');
      const result = await testFn({ email: testEmail, customerName: testName, sendImmediately });
      const data = result.data as { success: boolean; orderId: string };

      setTestResult({ success: true, message: `Test automation queued with order ID: ${data.orderId}` });
      setTestEmail('');
      setTestName('');

      // Refresh queue after test
      fetchQueueItems();
      fetchStats();
    } catch (error: any) {
      console.error('Test automation error:', error);
      setTestResult({ success: false, message: error.message || 'Failed to queue test automation' });
    } finally {
      setTesting(false);
    }
  };

  const handleProcessNow = async () => {
    setProcessing(true);
    setProcessResult(null);

    try {
      const processFn = httpsCallable(functions, 'processAutomationQueueNow');
      const result = await processFn({});
      const data = result.data as { success: boolean; sent: number; failed: number };

      setProcessResult({
        success: true,
        message: `Processed: ${data.sent || 0} sent, ${data.failed || 0} failed`
      });

      // Refresh queue after processing
      fetchQueueItems();
      fetchStats();
    } catch (error: any) {
      console.error('Process queue error:', error);
      setProcessResult({ success: false, message: error.message || 'Failed to process queue' });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
            <Clock size={12} /> Pending
          </span>
        );
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
            <CheckCircle size={12} /> Sent
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
            <XCircle size={12} /> Failed
          </span>
        );
      default:
        return null;
    }
  };

  const getTemplateLabel = (templateKey: string) => {
    const labels: Record<string, string> = {
      post_purchase_checkin: 'Check-in (Day 14)',
      post_purchase_review: 'Review Request (Day 21)',
      post_purchase_care: 'Care Tips (Day 30)'
    };
    return labels[templateKey] || templateKey;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Email Automations</h2>
          <p className="text-gray-500 text-sm">Post-purchase email sequences for apparel orders</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleProcessNow}
            disabled={processing || stats.pending === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-black text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {processing ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Processing...
              </>
            ) : (
              <>
                <Zap size={16} />
                Process Now
              </>
            )}
          </button>
          <button
            onClick={() => { fetchQueueItems(); fetchStats(); }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border border-gray-300 hover:border-black transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>
      {processResult && (
        <div className={`p-3 rounded-sm text-sm ${processResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {processResult.message}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <Clock className="text-yellow-600" size={24} />
            <div>
              <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
              <p className="text-sm text-yellow-600">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-600" size={24} />
            <div>
              <p className="text-2xl font-bold text-green-800">{stats.sent}</p>
              <p className="text-sm text-green-600">Sent</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <XCircle className="text-red-600" size={24} />
            <div>
              <p className="text-2xl font-bold text-red-800">{stats.failed}</p>
              <p className="text-sm text-red-600">Failed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Automation */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
        <div className="flex items-start gap-3">
          <TestTube className="text-blue-600 mt-1" size={20} />
          <div className="flex-1">
            <h3 className="font-bold mb-3">Test Automation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Send test post-purchase emails without going through the checkout flow.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                <input
                  type="text"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="Test Customer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendImmediately}
                  onChange={(e) => setSendImmediately(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Send immediately (skip 14/21/30 day delays)</span>
              </label>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleTestAutomation}
                disabled={testing || !testEmail}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {testing ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Testing...
                  </>
                ) : (
                  <>
                    <TestTube size={16} />
                    Test Automation
                  </>
                )}
              </button>
              {testResult && (
                <p className={`text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {testResult.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Automation Info */}
      <div className="bg-gray-50 border border-gray-200 p-4 rounded-sm">
        <div className="flex items-start gap-3">
          <Play className="text-green-600 mt-1" size={20} />
          <div>
            <h3 className="font-bold">Post-Purchase Sequence (Apparel)</h3>
            <p className="text-sm text-gray-600 mt-1">
              Automatically sends emails to customers who purchase apparel items:
            </p>
            <ul className="text-sm text-gray-600 mt-2 space-y-1">
              <li>• <strong>Day 14:</strong> "How's your order?" - Check-in email</li>
              <li>• <strong>Day 21:</strong> "We'd love your feedback" - Review request</li>
              <li>• <strong>Day 30:</strong> "Care tips for your new gear" - Product care</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {(['all', 'pending', 'sent', 'failed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
              filter === f
                ? 'border-black text-black'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Queue Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading queue...</p>
        </div>
      ) : queueItems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Mail size={48} className="mx-auto mb-4 opacity-50" />
          <p>No automation emails in queue</p>
          <p className="text-sm mt-2">Emails will appear here when customers place apparel orders</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wider text-xs text-gray-500">Email</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wider text-xs text-gray-500">Type</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wider text-xs text-gray-500">Order</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wider text-xs text-gray-500">Scheduled For</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wider text-xs text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {queueItems.map((item) => (
                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <p className="font-medium">{item.email}</p>
                    <p className="text-xs text-gray-500">{item.subject}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {getTemplateLabel(item.templateKey)}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs">{item.orderId}</td>
                  <td className="py-3 px-4 text-gray-600">{formatDate(item.scheduledFor)}</td>
                  <td className="py-3 px-4">
                    {getStatusBadge(item.status)}
                    {item.error && (
                      <p className="text-xs text-red-500 mt-1">{item.error}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AutomationManager;
